import { useTranslation } from "react-i18next";
import { Input } from "@/shared/components/ui/input";
import {
  PRODUCT_ACCESS_OPTIONS,
  PRODUCT_TIERS,
  type ProductAccess,
  type ProductTier,
} from "./constants";
import {
  catalogReadiness,
  isRankInputValid,
  valueToProductMetadata,
  type ProductMetadataValue,
} from "./product-metadata";

interface ProductMetadataFieldsProps {
  readonly value: ProductMetadataValue;
  readonly onChange: (next: ProductMetadataValue) => void;
  readonly disabled?: boolean;
  /** Prefix for input ids + `data-testid`s, e.g. `create-product`. */
  readonly idPrefix: string;
}

const LABEL_CLASS = "font-sans text-sm font-medium text-foreground";
const HINT_CLASS = "font-sans text-xs font-normal text-muted-foreground";
const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

/**
 * The pricing-v2 `tier` / `access` / `rank` metadata block, shared by the
 * create and update product dialogs. Fully controlled — the parent owns
 * the `ProductMetadataValue` state and the rank-validity gate.
 */
export function ProductMetadataFields({
  value,
  onChange,
  disabled,
  idPrefix,
}: ProductMetadataFieldsProps) {
  const { t } = useTranslation();
  const rankValid = isRankInputValid(value.rank);
  const readiness = catalogReadiness(valueToProductMetadata(value));

  const toggleAccess = (option: ProductAccess) => {
    const enabled = !value.access.includes(option);
    const next = PRODUCT_ACCESS_OPTIONS.filter((opt) =>
      opt === option ? enabled : value.access.includes(opt),
    );
    onChange({ ...value, access: next });
  };

  return (
    <fieldset
      data-testid={`${idPrefix}-metadata`}
      disabled={disabled}
      className="space-y-3 rounded-lg border border-border p-3"
    >
      <legend className={`px-1 ${LABEL_CLASS}`}>{t("catalog.products.metadata.heading")}</legend>
      <p className={HINT_CLASS}>{t("catalog.products.metadata.hint")}</p>

      <div className="space-y-1.5">
        <label htmlFor={`${idPrefix}-tier`} className={LABEL_CLASS}>
          {t("catalog.products.metadata.tierLabel")}
        </label>
        <select
          id={`${idPrefix}-tier`}
          data-testid={`${idPrefix}-tier-input`}
          value={value.tier}
          onChange={(e) => onChange({ ...value, tier: e.target.value as ProductTier | "" })}
          disabled={disabled}
          className={SELECT_CLASS}
        >
          <option value="">{t("catalog.products.metadata.tierNone")}</option>
          {PRODUCT_TIERS.map((tier) => (
            <option key={tier} value={tier}>
              {tier}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <span className={LABEL_CLASS}>{t("catalog.products.metadata.accessLabel")}</span>
        <div className="flex flex-wrap gap-4">
          {PRODUCT_ACCESS_OPTIONS.map((option) => (
            <label key={option} className="flex items-center gap-2 font-sans text-sm">
              <input
                type="checkbox"
                data-testid={`${idPrefix}-access-${option}`}
                checked={value.access.includes(option)}
                onChange={() => toggleAccess(option)}
                disabled={disabled}
              />
              <span>{t(`catalog.products.metadata.access.${option}`)}</span>
            </label>
          ))}
        </div>
        <p className={HINT_CLASS}>{t("catalog.products.metadata.accessHint")}</p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor={`${idPrefix}-rank`} className={LABEL_CLASS}>
          {t("catalog.products.metadata.rankLabel")}
        </label>
        <Input
          id={`${idPrefix}-rank`}
          data-testid={`${idPrefix}-rank-input`}
          type="number"
          inputMode="numeric"
          min="1"
          step="1"
          value={value.rank}
          onChange={(e) => onChange({ ...value, rank: e.target.value })}
          disabled={disabled}
          aria-invalid={!rankValid || undefined}
          aria-describedby={`${idPrefix}-rank-hint`}
        />
        <p id={`${idPrefix}-rank-hint`} className={HINT_CLASS}>
          {t("catalog.products.metadata.rankHint")}
        </p>
        {!rankValid && (
          <p
            data-testid={`${idPrefix}-rank-error`}
            role="alert"
            className="font-sans text-xs font-medium text-destructive"
          >
            {t("catalog.products.metadata.rankError")}
          </p>
        )}
      </div>

      {readiness === "incomplete" && (
        <p
          data-testid={`${idPrefix}-metadata-incomplete`}
          role="alert"
          className="font-sans text-xs font-medium text-amber-700"
        >
          {t("catalog.products.metadata.incompleteWarning")}
        </p>
      )}
    </fieldset>
  );
}
